/* ============================================================
   Модалка «Заявка на турнир»: поля рендерятся по схеме формы,
   собранной в админке (турнир.form.fields). Отправка — только
   с аккаунтом (POST /api/tournament/apply-fed).
   ============================================================ */
import { useRef, useState } from 'react';
import { useAppStore } from '../../store';
import { useFedStore, useL } from '../store';
import { useFedContent } from '../content';
import { tl } from '../data';
import { Ic } from '../icons';
import { Field } from '../ui';

const MAX_FILE_MB = 10;

function FileField({ label, required, file, onFile, hint }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) return; // тихо не берём — подсказка в hint
    onFile(f);
  };
  return (
    <div>
      <div className="fed-label mb-[9px]">{label}{required ? ' *' : ''}</div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className="flex items-center gap-3 rounded-xl cursor-pointer"
        style={{
          padding: '13px 15px',
          border: `1.5px dashed ${drag ? 'var(--fed-accent-bright)' : file ? 'rgba(67,217,106,.5)' : 'var(--fed-line-2)'}`,
          background: drag ? 'rgba(225,18,23,.06)' : 'var(--fed-surface-2)',
        }}
      >
        <span style={{ color: file ? 'var(--fed-ok)' : 'var(--fed-fg-muted)', display: 'inline-flex' }}>
          <Ic name={file ? 'check' : 'doc'} size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-fg truncate" style={{ fontSize: 13.5 }}>{file ? file.name : hint}</div>
        </div>
        {file && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFile(null); if (inputRef.current) inputRef.current.value = ''; }}
            className="bg-transparent border-none cursor-pointer text-fg-faint hover:text-accent-bright"
            style={{ fontSize: 16, lineHeight: 1 }}
          >
            ×
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp"
          className="hidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

export default function FedApplyModal() {
  const { applyOpen, applySent, applyTourId, closeApply, setApplySent, lang } = useFedStore();
  const L = useL();
  const tournaments = useFedContent((s) => s.tournaments);
  const submitFedApplication = useAppStore((s) => s.submitFedApplication);
  const fire = useAppStore((s) => s.fire);

  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);

  if (!applyOpen) return null;

  const tour = tournaments.find((t) => t.id === applyTourId);
  const fields = tour?.form?.fields || [];
  const lab = (f) => f['label_' + lang] || f.label_ru || f.key;

  const close = () => { closeApply(); setValues({}); setFiles({}); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    for (const f of fields) {
      if (!f.required) continue;
      if (f.type === 'file') {
        if (!files[f.key]) { fire(`${L.ap_required}: ${lab(f)}`, 'error'); return; }
      } else if (f.type === 'checkbox') {
        if (!values[f.key]) { fire(`${L.ap_required}: ${lab(f)}`, 'error'); return; }
      } else if (!String(values[f.key] || '').trim()) {
        fire(`${L.ap_required}: ${lab(f)}`, 'error'); return;
      }
    }
    setBusy(true);
    try {
      await submitFedApplication({ tournamentKey: applyTourId, answers: values, files });
      setApplySent();
      setValues({});
      setFiles({});
    } catch (err) { /* тост показывает стор */ }
    setBusy(false);
  };

  return (
    <div
      onClick={close}
      className="fixed inset-0 flex items-center justify-center p-5"
      style={{ zIndex: 300, background: 'rgba(4,4,6,.74)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)', animation: 'fedPageIn .28s var(--fed-ease) both' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[470px] overflow-y-auto rounded-[20px] border border-line-2 shadow-card-lg"
        style={{ maxHeight: '92vh', background: 'linear-gradient(165deg,var(--fed-surface-2),var(--fed-surface))', animation: 'fedRvIn .35s var(--fed-spring) both' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 82% -12%,rgba(225,18,23,.24),transparent 55%)' }} />
        <button
          onClick={close}
          className="absolute z-[3] w-[34px] h-[34px] rounded-[9px] border border-line bg-surface-2 text-fg-muted cursor-pointer"
          style={{ top: 14, right: 14 }}
        >
          <Ic name="x" size={15} sw={2.2} />
        </button>

        {!applySent ? (
          <div className="relative p-[30px]">
            <div
              className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-4 text-accent-bright"
              style={{ background: 'rgba(225,18,23,.1)', border: '1px solid var(--fed-line-accent)' }}
            >
              <Ic name="trophy" size={24} sw={1.8} />
            </div>
            <h2 className="font-body font-extrabold text-fg" style={{ fontSize: 26, textTransform: 'none', letterSpacing: 0 }}>{L.ap_title}</h2>
            <p className="text-fg-muted mt-2.5" style={{ fontSize: 14, lineHeight: 1.6 }}>
              {tl(tour, 'title', lang)} · {L.ap_sub}
            </p>

            <form onSubmit={onSubmit} className="flex flex-col gap-[15px] mt-[22px]">
              {fields.map((f) => {
                if (f.type === 'file') {
                  return (
                    <FileField
                      key={f.key}
                      label={lab(f)}
                      required={f.required}
                      file={files[f.key] || null}
                      onFile={(file) => setFiles({ ...files, [f.key]: file })}
                      hint={L.ap_file_hint}
                    />
                  );
                }
                if (f.type === 'checkbox') {
                  return (
                    <label key={f.key} className="flex gap-2.5 items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!values[f.key]}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })}
                        className="flex-shrink-0"
                        style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--fed-accent)' }}
                      />
                      <span className="text-fg-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                        {lab(f)}{f.required ? ' *' : ''}
                      </span>
                    </label>
                  );
                }
                if (f.type === 'select') {
                  return (
                    <Field key={f.key} label={`${lab(f)}${f.required ? ' *' : ''}`}>
                      <select
                        className="fed-input"
                        value={values[f.key] || ''}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      >
                        <option value="">—</option>
                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  );
                }
                return (
                  <Field key={f.key} label={`${lab(f)}${f.required ? ' *' : ''}`}>
                    <input
                      className="fed-input"
                      type={f.type === 'phone' ? 'tel' : 'text'}
                      placeholder={f.type === 'phone' ? '+7 ___ ___ __ __' : ''}
                      value={values[f.key] || ''}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    />
                  </Field>
                );
              })}
              <button type="submit" disabled={busy} className="fed-btn-primary h-[52px]" style={{ fontSize: 16, opacity: busy ? 0.7 : 1 }}>
                {busy ? '…' : L.ap_send}
              </button>
            </form>
          </div>
        ) : (
          <div className="relative text-center flex flex-col items-center gap-3.5" style={{ padding: '44px 30px' }}>
            <div
              className="w-[74px] h-[74px] rounded-full flex items-center justify-center"
              style={{ background: 'rgba(67,217,106,.14)', border: '1px solid rgba(67,217,106,.45)', color: 'var(--fed-ok)', boxShadow: '0 0 34px -10px rgba(67,217,106,.5)' }}
            >
              <Ic name="check" size={34} sw={2.4} />
            </div>
            <h2 className="font-body font-extrabold text-fg" style={{ fontSize: 26, textTransform: 'none', letterSpacing: 0 }}>{L.ap_ok_t}</h2>
            <p className="text-fg-muted max-w-[320px]" style={{ fontSize: 15, lineHeight: 1.6 }}>{L.ap_ok_d}</p>
            <button onClick={close} className="fed-btn-ghost h-12 px-[26px] mt-2 rounded-[11px]" style={{ fontSize: 15 }}>{L.ap_close}</button>
          </div>
        )}
      </div>
    </div>
  );
}
